import { Parse } from 'src/boot/parse'
import { Notify, Platform, LocalStorage } from 'quasar'
import { i18n } from 'src/boot/i18n'
import languages from 'quasar/lang/index.json'

export function resetVerificationCode ({ commit }) {
  commit('AUTH_VERIFICATION_CODE_SET', false)
}

export function sendVerificationCode ({ commit, rootState }, { countryCode, phoneNumber, country }) {
  commit('AUTH_LOADING', true)
  return Parse.Cloud
    .run('sendVerificationCode', {
      from: (rootState.business.business && rootState.business.business.attributes && Object.keys(rootState.business.business).length)
        ? rootState.business.business.get('title_en') : 'DreamDiner',
      country: country,
      phoneNumber: countryCode + phoneNumber.replace(/^0+/, '')
    }).then((code) => {
      commit('AUTH_VERIFICATION_CODE_SET', code)
    }).catch(error => {
      console.log('#### Auth error: ', error)
    }).finally(() => {
      commit('AUTH_LOADING', false)
    })
}

export function sendUSAVerificationCode ({ commit, rootState }, { countryCode, phoneNumber, country }) {
  commit('AUTH_LOADING', true)
  return Parse.Cloud
    .run('sendUSAVerificationCode', {
      from: (rootState.business.business && rootState.business.business.attributes && Object.keys(rootState.business.business).length)
        ? rootState.business.business.get('title_en') : 'DreamDiner',
      country: country,
      phoneNumber: countryCode + phoneNumber.replace(/^0+/, '')
    }).then((code) => {
      commit('AUTH_VERIFICATION_CODE_SET', code)
    }).catch(error => {
      console.log('#### Auth error: ', error)
    }).finally(() => {
      commit('AUTH_LOADING', false)
    })
}

export async function verifyAdminPin ({ commit }, pin) {
  commit('AUTH_LOADING', true)
  await new Parse.Query('Business')
    .equalTo('pin', pin)
    .find()
    .then(res => {
      if (res.length) {
        commit('AUTH_VERIFIED')
      } else {
        Notify.create({
          type: 'negative',
          message: 'Incorrect code'
        })
      }
    })
  commit('AUTH_LOADING', false)
}

export function checkUserExists ({ commit }, username) {
  return new Parse.Query('User')
    .equalTo('username', username.replace(/^0+/, ''))
    .find()
}

// Test func for push n
// const userId = "7YZUofNDjK"
// export function runPushTest ({ state }) {
//   console.log('run test', userId)
//   Parse.Cloud.run('sendMePush', { userId: userId })
// }

export async function initPushNotifications ({ commit, state }) {
  const { FCM } = require('@capacitor-community/fcm')
  const { PushNotifications } = require('@capacitor/push-notifications')

  let permStatus = await PushNotifications.checkPermissions();

  if (permStatus.receive === 'prompt') {
    permStatus = await PushNotifications.requestPermissions();
  }

  if (permStatus.receive !== 'granted') {
    Notify.create({
      type: 'negative',
      message: i18n.global.t('auth.notification_alert')
    })
  }

  // Register with Apple / Google to receive push via APNS/FCM
  if (Platform.is.ios) {
    PushNotifications.addListener('registration', () => {
      FCM.getToken().then(result => Parse.User.current().set('fcm_token', result.token).save())
    })
  } else if (Platform.is.android) {
    PushNotifications.addListener('registration', result => {
      Parse.User.current().set('fcm_token', result.value).save()
    })
  }
  await PushNotifications.addListener('pushNotificationReceived', notification => { });

  await PushNotifications.addListener('pushNotificationActionPerformed', notification => { });
  PushNotifications.register()
}

export function getLocation ({ commit, state }, location = false) {
  return new Promise((resolve, reject) => {
    if (location !== false) {
      location = new Parse.GeoPoint(location)
      commit('AUTH_SET_LOCATION', location)
      resolve(location)
    } else if (state.location !== false) {
      resolve(state.location)
    } else {
      if (Platform.is.capacitor) {
        const { Geolocation } = require('@capacitor/geolocation')
        Geolocation.getCurrentPosition().then(({ coords }) => {
          location = new Parse.GeoPoint(coords)
          commit('AUTH_SET_LOCATION', location)
          resolve(location)
        }).catch((e) => {
          console.warn('getLocation error:', e)
          resolve(false)
        })
      } else {
        navigator.geolocation.getCurrentPosition(({ coords }) => {
          location = new Parse.GeoPoint(coords)
          commit('AUTH_SET_LOCATION', location)
          resolve(location)
        }, (e) => {
          console.warn('getLocation error:', e)
          resolve(false)
        }, { enableHighAccuracy: true })
      }
    }
  })
}

export async function logIn ({ state, commit, dispatch, rootState },
  { username, name, userExists, countryCode = false, phone = null, country = null }) {
  commit('AUTH_LOADING', true)

  if (!phone) {
    phone = username.replace(countryCode, '')
  }

  let platform = 'cl_web'
  if (Platform.is.capacitor) {
    if (Platform.is.ios) {
      platform += '_ios'
    } else if (Platform.is.android) {
      platform += '_and'
    }
  }

  if (!userExists) {
    const language = LocalStorage.getItem('dreamdiner.locale')
    const langNumber = state.languages.findIndex(lang => lang === language)
    const user = new Parse.User({
      username: username.replace(/^0+/, ''),
      password: username.replace(/^0+/, ''),
      phone: phone,
      name: name,
      registered_using: platform,
      blocked: false,
      is_admin: false,
      country: country,
      language: langNumber >= 0 ? langNumber : 0
    })
    try {
      await user.signUp()
    } catch (e) {
      Notify.create({
        type: 'negative',
        message: 'Error: ' + e.code + ' ' + e.message
      })
    }
  }

  await Parse.User.logIn(username.replace(/^0+/, ''), username.replace(/^0+/, ''), { usePost: true }).then(user => {
    // console.log('user', user)
    commit('AUTH_TOKEN_SET', user.get('sessionToken'))
    dispatch('afterLogin')
  })

  commit('AUTH_LOADING', false)
}

export function afterLogin ({ dispatch }) {
  dispatch('updateLoggedUser')
  // dispatch('getLocation')
  if (Platform.is.capacitor) {
    dispatch('initPushNotifications')
  }
}

export async function logout ({ commit, dispatch }) {
  console.log('logout')
  await Parse.User.logOut()
  await dispatch('cart/removeItems', null, { root: true })
  await dispatch('orders/destroy', false, { root: true })

  commit('AUTH_LOGOUT')
}

export async function getAddresses ({ commit }) {
  commit('AUTH_LOADING', true)
  await new Parse.Query('Address')
    .equalTo('client', Parse.User.current())
    .find()
    .then(res => {
      commit('AUTH_STORE_ADDRESSES', res)
    })
  commit('AUTH_LOADING', false)
}

export async function updateLoggedUser ({ commit, dispatch }, userData = null) {
  commit('AUTH_LOADING', true)
  const user = Parse.User.current()
  if (userData) {
    await user.set(userData).save()
  }

  commit('AUTH_STORE_USER', user)
  commit('AUTH_LOADING', false)
}

export function refresh ({ state, commit, dispatch }) {
  if (state.token && state.token.length) {
    Parse.User
      .become(state.token)
      .then(() => dispatch('afterLogin'))
      .catch(() => dispatch('logout'))
  }

  commit('AUTH_CHECKED')
}

export async function staffLogin ({ state, commit, dispatch }, username) {
  let user = null

  await Parse.User.logIn(username.replace(/^0+/, ''),
    username.replace(/^0+/, ''), { usePost: true })
    .then(result => {
      user = result
      commit('AUTH_TOKEN_SET', user.get('sessionToken'))
    })

  return user
}

export async function findUserByStaff ({ state, commit, dispatch }, ID) {
  // if (Parse.User.current() && Parse.User.current().id !== ID) {
  //   console.log('findUserByStaff current user is different (logout)', Parse.User.current().id)
  //   await dispatch('logout')
  // }
  return await Parse.Cloud
    .run('findUserByStaff', {
      staffId: ID
    }).then((result) => {
      return result
    }).catch(async (error) => {
      if (Parse.User.current()) {
        return await dispatch('logout')
      } else {
        return error
      }
    })
}

const defaultBoard = {
  category: true,
  tray: true,
  order: true,
  tracker: true,
  payment: true
}

// Resolve a locale string (either a base code like "nb", or a full one like "nb-NO"
// from navigator.language / localStorage) to the Quasar lang entry we should load.
// Returns false when the app does not ship that language.
//
// ⚠️ This used to build `${locale}-${getFlagByLang(locale)}` and compare it
// case-sensitively against Quasar's isoName. Two things were wrong: a FLAG code is not
// a REGION code (they only coincide by luck for our set), and the built string was
// lowercase while Quasar's isoName is region-cased ("nb-NO", "zh-CN", "ko-KR", "pt-BR").
// It therefore never matched, and boot-time locale restore silently did nothing for
// every multi-part locale. Fixed 2026-08-09 — see the client-findappfulllocale-case-bug note.
export function findAppFullLocale({ state }, locale) {
  if (!locale) return false

  const wanted = String(locale).toLowerCase()
  const shortLocale = wanted.split('-')[0]

  // state.languages holds BASE codes, and its index IS the server's language index.
  if (!state.languages.includes(shortLocale)) return false

  // Pick the variant we actually SHIP rather than whichever Quasar lists first, or the
  // bare pack when we bundle only a regional one. These must match the webpackInclude
  // regex in Select.vue / LanguageSelect.vue / layouts/Main.vue — a pack outside that
  // regex is not bundled and the dynamic import throws at runtime.
  //   en → en-US (not en-GB, which Quasar lists first)
  //   zh → zh-CN
  //   pt → pt-BR (a bare "pt" pack exists but is NOT bundled; our flag for pt is 'br')
  // The langOptions getter has the same map minus pt — keep them in sync.
  const PREFERRED = { en: 'en-US', zh: 'zh-CN', pt: 'pt-BR' }

  return (
    // PREFERRED wins outright for its base, whatever region the caller asked for — those
    // are the only variants we bundle. So en-GB → en-US, zh-TW → zh-CN, bare pt → pt-BR.
    languages.find(lang => lang.isoName === PREFERRED[shortLocale]) ||
    // otherwise an exact match on what the caller gave us ("nb-NO" → nb-NO)
    languages.find(lang => lang.isoName.toLowerCase() === wanted) ||
    // a pack whose isoName is the bare base ("de" → de)
    languages.find(lang => lang.isoName.toLowerCase() === shortLocale) ||
    // otherwise any pack in that base's family ("nb" → nb-NO, "ko" → ko-KR)
    languages.find(lang => lang.isoName.split('-')[0].toLowerCase() === shortLocale) ||
    false
  )
}
